import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs';
import { UserService } from '../../../user/service/user.service';
import { DutyTemplateService } from '../../services/duty-template.service';
import { ParentButtonComponent } from '../../../../shared/components/ui/parent-button/parent-button.component';
import { InitialsPipe } from '../../../../shared/pipes/initials.pipe';
import { ParentFamilyContextService } from '../../../parent-family/parent-family-context.service';
import { FamilyKidsService } from '../../../parent-family/services/family-kids.service';
import { HomeDialogService } from '../../services/home-dialog.service';
import type { RoutineBuilderDialogData } from '../routine-builder-dialog/routine-builder-dialog.component';
import type { DutyTemplate, DutyTemplateItem } from '../../models/duty-template.model';
import {
  PREDEFINED_ROUTINES,
  type RoutineSelection,
} from '../../models/predefined-routine.model';
import {
  findPredefinedRoutine,
  resolvePredefinedRoutine,
} from '../../helpers/predefined-routine.helper';
import { GaEventService } from '../../../../core/services/ga.service';
import {
  PARENT_ANALYTICS_LAYOUT,
  ParentAnalyticsEvents,
} from '../../../../core/analytics/parent-analytics.events';

interface RoutinePreview {
  name: string;
  items: DutyTemplateItem[];
}

@Component({
  selector: 'app-apply-duty-template-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    TranslatePipe,
    ParentButtonComponent,
    InitialsPipe,
  ],
  templateUrl: './apply-duty-template-dialog.component.html',
  styleUrl: './apply-duty-template-dialog.component.scss',
})
export class ApplyDutyTemplateDialogComponent implements OnInit {
  private readonly dialogRef = inject(
    MatDialogRef<ApplyDutyTemplateDialogComponent>,
  );
  #userService = inject(UserService);
  #templates = inject(DutyTemplateService);
  #family = inject(ParentFamilyContextService);
  #familyKids = inject(FamilyKidsService);
  #homeDialogs = inject(HomeDialogService);
  #translate = inject(TranslateService);
  #analytics = inject(GaEventService);

  readonly starters = PREDEFINED_ROUTINES;

  selectedRoutine = signal<RoutineSelection | null>(null);
  selectedKids = signal<string[]>([]);
  isSubmitting = signal(false);
  #initialKidSyncDone = false;

  readonly user = computed(() => this.#userService.user());
  readonly kids = this.#familyKids.children;
  readonly savedTemplates = computed(
    () => this.#templates.templatesQuery.data() ?? [],
  );
  readonly isLoadingSaved = computed(
    () => this.#templates.templatesQuery.isPending(),
  );
  readonly selectedKidsCount = computed(() => this.selectedKids().length);
  readonly allKidsSelected = computed(
    () =>
      this.kids().length > 0 &&
      this.selectedKids().length === this.kids().length,
  );
  readonly showKidBulkActions = computed(() => this.kids().length >= 3);

  readonly selectedPreview = computed((): RoutinePreview | null => {
    const selection = this.selectedRoutine();
    if (!selection) return null;

    if (selection.kind === 'starter') {
      const starter = findPredefinedRoutine(selection.starterId);
      if (!starter) return null;
      return resolvePredefinedRoutine(starter, this.#translate);
    }

    const template = this.savedTemplates().find(
      (entry) => entry.id === selection.templateId,
    );
    if (!template) return null;

    return {
      name: template.name,
      items: template.items ?? [],
    };
  });

  readonly canApply = computed(
    () =>
      !!this.selectedRoutine() &&
      this.selectedKids().length > 0 &&
      !!this.selectedPreview() &&
      !this.isSubmitting(),
  );

  readonly canCustomize = computed(() => !!this.selectedPreview());

  constructor() {
    effect(() => {
      this.kids();
      this.syncInitialKidSelection();
    });
  }

  ngOnInit(): void {
    void this.#templates.templatesQuery.refetch();
    const firstStarter = this.starters[0];
    if (firstStarter) {
      this.selectedRoutine.set({
        kind: 'starter',
        starterId: firstStarter.id,
      });
    }
  }

  isKidSelected(kidId: string): boolean {
    return this.selectedKids().includes(kidId);
  }

  isStarterSelected(starterId: string): boolean {
    const selection = this.selectedRoutine();
    return selection?.kind === 'starter' && selection.starterId === starterId;
  }

  isSavedSelected(templateId: string): boolean {
    const selection = this.selectedRoutine();
    return selection?.kind === 'saved' && selection.templateId === templateId;
  }

  toggleKidSelection(kidId: string): void {
    this.selectedKids.update((current) =>
      current.includes(kidId)
        ? current.filter((id) => id !== kidId)
        : [...current, kidId],
    );
  }

  toggleAllKids(): void {
    if (this.allKidsSelected()) {
      this.selectedKids.set([]);
      return;
    }
    this.selectedKids.set(this.kids().map((kid) => kid.id));
  }

  selectStarter(starterId: string): void {
    this.selectedRoutine.set({ kind: 'starter', starterId });
  }

  selectSaved(template: DutyTemplate): void {
    this.selectedRoutine.set({ kind: 'saved', templateId: template.id });
  }

  onCreateCustom(): void {
    this.openBuilderReplacing({ mode: 'create' });
  }

  onCustomize(): void {
    const preview = this.selectedPreview();
    const selection = this.selectedRoutine();
    if (!preview || !selection) return;

    if (selection.kind === 'saved') {
      const template = this.savedTemplates().find(
        (entry) => entry.id === selection.templateId,
      );
      if (template) {
        this.openBuilderReplacing({
          mode: 'edit',
          template,
        });
      }
      return;
    }

    this.openBuilderReplacing({
      mode: 'create',
      prefill: {
        name: preview.name,
        items: preview.items,
      },
    });
  }

  onEditSaved(template: DutyTemplate, event: Event): void {
    event.stopPropagation();
    this.openBuilderReplacing({
      mode: 'edit',
      template,
    });
  }

  onDiscard(): void {
    this.dialogRef.close();
  }

  async onDeleteSaved(templateId: string, event: Event): Promise<void> {
    event.stopPropagation();
    await this.#templates.deleteTemplate.mutateAsync(templateId);
    const selection = this.selectedRoutine();
    if (
      selection?.kind === 'saved' &&
      selection.templateId === templateId
    ) {
      const firstStarter = this.starters[0];
      this.selectedRoutine.set(
        firstStarter
          ? { kind: 'starter', starterId: firstStarter.id }
          : null,
      );
    }
  }

  async onApply(): Promise<void> {
    const selection = this.selectedRoutine();
    const preview = this.selectedPreview();
    const kidIds = this.selectedKids();
    if (!selection || !preview || kidIds.length === 0) return;

    this.isSubmitting.set(true);
    try {
      this.syncFamilyScopeAfterApply(kidIds);
      if (selection.kind === 'starter') {
        await this.#templates.applyResolvedRoutine(
          preview.name,
          preview.items,
          kidIds,
        );
      } else {
        await this.#templates.applyTemplate.mutateAsync({
          templateId: selection.templateId,
          kidIds,
        });
      }
      this.#analytics.sendEvent(ParentAnalyticsEvents.APPLY_DUTY, {
        layout: PARENT_ANALYTICS_LAYOUT,
        routine_kind: selection.kind,
        kid_count: kidIds.length,
        item_count: preview.items.length,
      });
      void this.#userService.reloadProfile();
      this.dialogRef.close(true);
    } finally {
      this.isSubmitting.set(false);
    }
  }

  itemCount(template: DutyTemplate): number {
    return template.items?.length ?? 0;
  }

  starterItemCount(starterId: string): number {
    return findPredefinedRoutine(starterId)?.items.length ?? 0;
  }

  private syncInitialKidSelection(): void {
    if (this.#initialKidSyncDone || this.selectedKids().length > 0) {
      return;
    }

    const children = this.kids();
    if (children.length === 0) return;

    this.#initialKidSyncDone = true;

    const familyChildId = this.#family.selectedChildId();
    if (familyChildId && children.some((kid) => kid.id === familyChildId)) {
      this.selectedKids.set([familyChildId]);
      return;
    }

    this.selectedKids.set([children[0]!.id]);
  }

  /** Board duties are scoped to the family bar kid — align it after apply. */
  private syncFamilyScopeAfterApply(kidIds: string[]): void {
    if (kidIds.length === 0) return;

    const current = this.#family.selectedChildId();
    if (current && kidIds.includes(current)) {
      return;
    }

    this.#family.selectChild(kidIds[0]!);
  }

  private openBuilderReplacing(data: RoutineBuilderDialogData): void {
    this.dialogRef
      .afterClosed()
      .pipe(take(1))
      .subscribe(() => {
        this.#homeDialogs.openRoutineBuilderDialog(data);
      });
    this.dialogRef.close();
  }
}
